import {gql} from '@apollo/client';

/**
 * 抽查任务筛选条件，与后端 PodExemptSpotTaskFiltersInput 一一对应。
 * 所有字段可空，为空即不参与筛选。
 */
export interface SpotTaskFiltersInput {
    statuses?: string[];
    conclusions?: string[];
    periodNo?: string;
    periodType?: string;
    targetType?: string;
    targetId?: number;
    source?: string;
    assigneeId?: number;
    dspId?: number;
    zipcode?: string;
}

/** 任务列表行，字段与后端 PodExemptSpotTaskType 对齐 */
export interface SpotTaskTypeGql {
    id: number;
    taskNo?: string;
    periodNo?: string;
    periodType?: string;
    targetType?: string;
    targetId?: number;
    targetNo?: string;
    targetAddress?: string;
    targetZipcode?: string;
    targetAddressCount?: number;
    dspId?: number;
    status?: string;
    source?: string;
    sourceDetail?: string;
    exemptCount?: number;
    planSampleCount?: number;
    sampleCount?: number;
    failCount?: number;
    assigneeId?: number;
    assigneeName?: string;
    doneAt?: string;
    conclusion?: string;
    conclusionRemark?: string;
    createdAt?: string;
}

export interface SpotSampleTypeGql {
    id: number;
    taskId?: number;
    orderId?: number;
    waybillNo?: string;
    judgeResult?: string;
    podImageUrls?: string[];
    driverName?: string;
    deliveredAt?: string;
    judgeRemark?: string;
    judgedBy?: number;
    judgedByName?: string;
    judgedAt?: string;
}

/** 任务字段片段：列表、详情、溯源三处共用，避免各写一份导致字段漂移 */
const SPOT_TASK_FIELDS = gql`
    fragment SpotTaskFields on PodExemptSpotTaskType {
        id
        taskNo
        periodNo
        periodType
        targetType
        targetId
        targetNo
        targetAddress
        targetZipcode
        targetAddressCount
        dspId
        status
        source
        sourceDetail
        exemptCount
        planSampleCount
        sampleCount
        failCount
        assigneeId
        assigneeName
        doneAt
        conclusion
        conclusionRemark
        createdAt
    }
`;

export const GET_SPOT_TASKS = gql`
    ${SPOT_TASK_FIELDS}
    query PodExemptSpotTasks($filters: PodExemptSpotTaskFiltersInput, $pageNum: Int!, $pageSize: Int!) {
        podExemptSpotTasks(filters: $filters, pageNum: $pageNum, pageSize: $pageSize) {
            content {
                ...SpotTaskFields
            }
            total
        }
    }
`;

export interface GetSpotTasksResponse {
    podExemptSpotTasks: {
        content: SpotTaskTypeGql[];
        total: number;
    };
}

/** 分组计数与列表共用筛选条件，保证 Tab 计数与列表条数口径一致 */
export const GET_SPOT_TASK_GROUP_COUNTS = gql`
    query PodExemptSpotTaskGroupCounts($filters: PodExemptSpotTaskFiltersInput) {
        podExemptSpotTaskGroupCounts(filters: $filters) {
            groupKey
            count
        }
    }
`;

export interface GetSpotTaskGroupCountsResponse {
    podExemptSpotTaskGroupCounts: Array<{groupKey: string; count: number}>;
}

/** 周期下拉数据源：只返回真有任务的期，倒序 */
export const GET_SPOT_PERIODS = gql`
    query PodExemptSpotPeriods {
        podExemptSpotPeriods
    }
`;

export interface GetSpotPeriodsResponse {
    podExemptSpotPeriods: string[];
}

export const GET_SPOT_TASK_STATS = gql`
    query PodExemptSpotTaskStats($periodNo: String) {
        podExemptSpotTaskStats(periodNo: $periodNo) {
            pendingCount
            monitorAnomalyCount
            randomSampleCount
            offlineCount
        }
    }
`;

export interface GetSpotTaskStatsResponse {
    podExemptSpotTaskStats: {
        pendingCount: number;
        monitorAnomalyCount: number;
        randomSampleCount: number;
        offlineCount: number;
    };
}

export const GET_SPOT_TASK_DETAIL = gql`
    ${SPOT_TASK_FIELDS}
    query PodExemptSpotTaskDetail($taskId: Long!) {
        podExemptSpotTaskDetail(taskId: $taskId) {
            task {
                ...SpotTaskFields
            }
            samples {
                id
                taskId
                orderId
                waybillNo
                judgeResult
                podImageUrls
                driverName
                deliveredAt
                judgeRemark
                judgedBy
                judgedByName
                judgedAt
            }
        }
    }
`;

export interface GetSpotTaskDetailResponse {
    podExemptSpotTaskDetail: {
        task?: SpotTaskTypeGql;
        samples: SpotSampleTypeGql[];
    };
}
